import React, { useEffect, useState } from "react";
import config from "@config";
import apiEndpoints from "@store/apiEndpoints";
import errorFunctions from "@store/errorFunctions";
import { SelectControl } from "./FormBits";

/*
Edit form for the ticket's return address, on the tn-modal-* shell from
ConfirmModal.js so it matches this screen. Same fields and location lookups as
CustomerDetail/.../AddressConfirmModal.js (which Warranties/SingleWarranty's
DeliveryAddress uses): AU addresses pick State and Suburb from the location
API (Postcode too, once the suburb has known postcodes); any other country is
free text. Country is shown but read-only.

Mount it only while open - the draft is seeded from `address` on mount.
*/
const COUNTRY_OPTIONS = [
  { value: "AU", label: "Australia" },
  { value: "NZ", label: "New Zealand" },
];

const FIELDS = ["address_line_one", "address_line_two", "suburb", "state", "postcode", "country"];

const toOptions = function (values) {
  return values.map(function (value) {
    return { value: value, label: value };
  });
};

// Keeps a saved value selectable before (or without) its lookup list loading -
// SelectControl only shows a value that matches one of its options.
const withCurrent = function (options, value) {
  if (!value) return options;
  const listed = options.some(function (option) {
    return option.value === value;
  });
  return listed ? options : [{ value: value, label: value }].concat(options);
};

const ReturnAddressModal = function (props) {
  const { address, busy, protectedFetch, onSave, onClose } = props;
  const [draft, setDraft] = useState(function () {
    return Object.assign({ country: config.country }, address);
  });
  const [stateOptions, setStateOptions] = useState([]);
  const [suburbOptions, setSuburbOptions] = useState([]);
  const [postcodeOptions, setPostcodeOptions] = useState([]);
  const [loadingSuburbs, setLoadingSuburbs] = useState(false);

  const isAu = (draft.country || "AU") === "AU";

  const lookup = function (url, onResult) {
    return protectedFetch(url, "GET", null, errorFunctions.checkResponseThenJson)
      .then(onResult)
      .catch(function (err) {
        console.log("Address lookup failed", err);
      });
  };

  const fetchStates = function (country) {
    lookup(apiEndpoints.location.states(country), function (res) {
      setStateOptions(
        (res || []).map(function (x) {
          return { value: x.code, label: x.name };
        })
      );
    });
  };

  const fetchSuburbs = function (state, query) {
    if (!state) return;
    setLoadingSuburbs(true);
    lookup(apiEndpoints.location.suburbs(state, "", query || ""), function (res) {
      setSuburbOptions(
        toOptions(
          ((res && res.results) || []).map(function (x) {
            return x.name;
          })
        )
      );
    }).then(function () {
      setLoadingSuburbs(false);
    });
  };

  // `autofill` fills the postcode when the suburb resolves to exactly one.
  const fetchPostcodes = function (suburb, state, autofill) {
    if (!suburb || !state) return;
    lookup(apiEndpoints.location.postcodes(suburb, state), function (res) {
      const codes = ((res && res.results) || []).map(function (x) {
        return x.code;
      });
      setPostcodeOptions(toOptions(codes));
      if (autofill && codes.length === 1) {
        setDraft(function (prev) {
          return Object.assign({}, prev, { postcode: codes[0] });
        });
      }
    });
  };

  // Prefill the lists from the saved address so its values are selectable.
  useEffect(function () {
    if (!isAu) return;
    fetchStates("AU");
    if (draft.state) fetchSuburbs(draft.state);
    if (draft.suburb && draft.state) fetchPostcodes(draft.suburb, draft.state);
    // Mount-only: later changes are fetched by the change handlers below.
  }, []);

  const setField = function (key, value, reset) {
    setDraft(function (prev) {
      const next = Object.assign({}, prev, { [key]: value });
      (reset || []).forEach(function (k) {
        next[k] = "";
      });
      return next;
    });
  };

  const handleStateChange = function (state) {
    setField("state", state, ["suburb", "postcode"]);
    setSuburbOptions([]);
    setPostcodeOptions([]);
    if (state) fetchSuburbs(state);
  };

  // A new suburb invalidates the old postcode.
  const handleSuburbChange = function (suburb) {
    setField("suburb", suburb, ["postcode"]);
    setPostcodeOptions([]);
    fetchPostcodes(suburb, draft.state, true);
  };

  // The suburb list is capped (page_size=100), so search server-side as the
  // user types, as AddressConfirmModal does.
  const handleSuburbInputChange = function (input, meta) {
    if (meta.action === "input-change" && input.length > 2) {
      fetchSuburbs(draft.state, input);
    }
  };

  const handleTextChange = function (e) {
    setField(e.target.name, e.target.value);
  };

  const isDirty = FIELDS.some(function (key) {
    return String(draft[key] || "") !== String((address && address[key]) || "");
  });

  const handleSubmit = function (e) {
    e.preventDefault();
    if (busy || !isDirty) return;
    onSave(draft);
  };

  const textInput = function (id, name, placeholder) {
    return (
      <input
        id={id}
        name={name}
        className="tn-input"
        placeholder={placeholder}
        value={draft[name] || ""}
        onChange={handleTextChange}
        disabled={busy}
      />
    );
  };

  return (
    <div className="tn-modal-overlay" onClick={busy ? undefined : onClose}>
      <form
        className="tn-modal tn-modal-wide"
        role="dialog"
        aria-modal="true"
        aria-label="Edit return address"
        onSubmit={handleSubmit}
        onClick={function (e) {
          e.stopPropagation();
        }}
      >
        <p className="tn-modal-title">Edit Return Address</p>
        <p className="tn-modal-body">
          Where the repaired or replaced item is sent back to. Only this
          ticket is updated, not the customer&apos;s account.
        </p>

        <div className="tn-fieldgrid tn-fieldgrid-2">
          <div className="tn-field">
            <label className="tn-field-label" htmlFor="tnAddrLineOne">
              Address Line 1
            </label>
            {textInput("tnAddrLineOne", "address_line_one", "Street address")}
          </div>
          <div className="tn-field">
            <label className="tn-field-label" htmlFor="tnAddrLineTwo">
              Address Line 2
            </label>
            {textInput("tnAddrLineTwo", "address_line_two", "Unit, building (optional)")}
          </div>

          <div className="tn-field">
            <label className="tn-field-label" htmlFor="tnAddrCountry">
              Country
            </label>
            <SelectControl
              inputId="tnAddrCountry"
              options={COUNTRY_OPTIONS}
              value={draft.country || "AU"}
              onChange={function () {}}
              isDisabled
            />
          </div>
          <div className="tn-field">
            <label className="tn-field-label" htmlFor="tnAddrState">
              State
            </label>
            {isAu ? (
              <SelectControl
                inputId="tnAddrState"
                placeholder="Search for a state…"
                options={withCurrent(stateOptions, draft.state)}
                value={draft.state || ""}
                onChange={handleStateChange}
                isDisabled={busy}
              />
            ) : (
              textInput("tnAddrState", "state", "State / region")
            )}
          </div>

          <div className="tn-field">
            <label className="tn-field-label" htmlFor="tnAddrSuburb">
              Suburb
            </label>
            {isAu ? (
              <SelectControl
                inputId="tnAddrSuburb"
                placeholder={draft.state ? "Search for a suburb…" : "Select a state first"}
                options={withCurrent(suburbOptions, draft.suburb)}
                value={draft.suburb || ""}
                onChange={handleSuburbChange}
                onInputChange={handleSuburbInputChange}
                isLoading={loadingSuburbs}
                isDisabled={busy || !draft.state}
              />
            ) : (
              textInput("tnAddrSuburb", "suburb", "Suburb")
            )}
          </div>
          <div className="tn-field">
            <label className="tn-field-label" htmlFor="tnAddrPostcode">
              Postcode
            </label>
            {/* Free text until the suburb has known postcodes to pick from,
                as in AddressConfirmModal. */}
            {isAu && postcodeOptions.length > 0 ? (
              <SelectControl
                inputId="tnAddrPostcode"
                placeholder="Select postcode…"
                options={withCurrent(postcodeOptions, draft.postcode)}
                value={draft.postcode || ""}
                onChange={function (postcode) {
                  setField("postcode", postcode);
                }}
                isDisabled={busy}
              />
            ) : (
              textInput("tnAddrPostcode", "postcode", "Postcode")
            )}
          </div>
        </div>

        <div className="tn-modal-actions">
          <button
            type="button"
            className="tn-btn tn-btn-ghost tn-btn-sm"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="tn-btn tn-btn-primary tn-btn-sm"
            disabled={busy || !isDirty}
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ReturnAddressModal;
